// Client-side (offline) comparison engine.
// Renders PDF pages via pdf.js and decodes TIFF frames via UTIF, then performs
// pixel-by-pixel comparison on <canvas> ImageData.
//
// Output shape mirrors the previous backend response so the UI stays unchanged:
//   {
//     success: true,
//     page_count: N,
//     pages: [{ overlay, base, width, height, stats }],
//     file_a_pages: number,
//     file_b_pages: number,
//   }
// `overlay` and `base` are base64-encoded PNGs (no data-URL prefix).

import * as pdfjsLib from 'pdfjs-dist';
import UTIF from 'utif';

// Point pdf.js at the worker copied into /public
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

const MAX_PAGES = 20;
const PDF_RENDER_SCALE = 2; // matches previous backend 2x quality
const THRESHOLD = 250; // grayscale value below which a pixel is considered "content"

function getExt(name) {
  return (name || '').toLowerCase().split('.').pop();
}

async function fileToPages(file) {
  const ext = getExt(file.name);
  const buf = await file.arrayBuffer();

  if (ext === 'pdf') {
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    const count = Math.min(pdf.numPages, MAX_PAGES);
    const pages = [];
    for (let i = 1; i <= count; i++) {
      // eslint-disable-next-line no-await-in-loop
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: PDF_RENDER_SCALE });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // eslint-disable-next-line no-await-in-loop
      await page.render({ canvasContext: ctx, viewport, background: '#FFFFFF' }).promise;
      pages.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
    }
    return pages;
  }

  if (ext === 'tif' || ext === 'tiff') {
    const ifds = UTIF.decode(buf);
    const count = Math.min(ifds.length, MAX_PAGES);
    const pages = [];
    for (let i = 0; i < count; i++) {
      const ifd = ifds[i];
      UTIF.decodeImage(buf, ifd);
      const rgba = UTIF.toRGBA8(ifd);
      const w = ifd.width;
      const h = ifd.height;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, w, h);
      const src = new ImageData(new Uint8ClampedArray(rgba), w, h);
      const tmp = document.createElement('canvas');
      tmp.width = w;
      tmp.height = h;
      tmp.getContext('2d').putImageData(src, 0, 0);
      ctx.drawImage(tmp, 0, 0);
      pages.push(ctx.getImageData(0, 0, w, h));
    }
    return pages;
  }

  if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) {
    const url = URL.createObjectURL(new Blob([buf]));
    try {
      const bmp = await loadImage(url);
      const canvas = document.createElement('canvas');
      canvas.width = bmp.width;
      canvas.height = bmp.height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bmp, 0, 0);
      return [ctx.getImageData(0, 0, canvas.width, canvas.height)];
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  throw new Error(`Unsupported file format: ${ext}`);
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = src;
  });
}

function padToCanvas(imgData, width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);
  const tmp = document.createElement('canvas');
  tmp.width = imgData.width;
  tmp.height = imgData.height;
  tmp.getContext('2d').putImageData(imgData, 0, 0);
  ctx.drawImage(tmp, 0, 0);
  return ctx.getImageData(0, 0, width, height);
}

async function canvasToBase64Png(canvas) {
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

async function comparePagePair(aImg, bImg) {
  const width = Math.max(aImg.width, bImg.width);
  const height = Math.max(aImg.height, bImg.height);

  const A = aImg.width === width && aImg.height === height ? aImg : padToCanvas(aImg, width, height);
  const B = bImg.width === width && bImg.height === height ? bImg : padToCanvas(bImg, width, height);

  const total = width * height;
  const overlay = new Uint8ClampedArray(total * 4);
  const base = new Uint8ClampedArray(total * 4);

  const aData = A.data;
  const bData = B.data;

  let overlapPx = 0;
  let onlyAPx = 0;
  let onlyBPx = 0;

  for (let i = 0; i < total; i++) {
    const p = i * 4;
    const ga = (aData[p] + aData[p + 1] + aData[p + 2]) / 3;
    const gb = (bData[p] + bData[p + 1] + bData[p + 2]) / 3;
    const hasA = ga < THRESHOLD;
    const hasB = gb < THRESHOLD;

    if (hasA && hasB) {
      overlay[p] = 0; overlay[p + 1] = 0; overlay[p + 2] = 0; overlay[p + 3] = 255;
      overlapPx++;
    } else if (hasA) {
      overlay[p] = 239; overlay[p + 1] = 68; overlay[p + 2] = 68; overlay[p + 3] = 255;
      onlyAPx++;
    } else if (hasB) {
      overlay[p] = 34; overlay[p + 1] = 197; overlay[p + 2] = 94; overlay[p + 3] = 255;
      onlyBPx++;
    }
    // else transparent

    const combined = Math.min(ga, gb);
    const g = Math.round(200 + (combined / 255) * 55);
    base[p] = g; base[p + 1] = g; base[p + 2] = g; base[p + 3] = 255;
  }

  const overlayCanvas = document.createElement('canvas');
  overlayCanvas.width = width;
  overlayCanvas.height = height;
  overlayCanvas.getContext('2d').putImageData(new ImageData(overlay, width, height), 0, 0);

  const baseCanvas = document.createElement('canvas');
  baseCanvas.width = width;
  baseCanvas.height = height;
  baseCanvas.getContext('2d').putImageData(new ImageData(base, width, height), 0, 0);

  const [overlayB64, baseB64] = await Promise.all([
    canvasToBase64Png(overlayCanvas),
    canvasToBase64Png(baseCanvas),
  ]);

  return {
    overlay: overlayB64,
    base: baseB64,
    width,
    height,
    stats: { total, overlap: overlapPx, only_a: onlyAPx, only_b: onlyBPx },
  };
}

function blankImageData(width, height) {
  const arr = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < arr.length; i += 4) {
    arr[i] = 255; arr[i + 1] = 255; arr[i + 2] = 255; arr[i + 3] = 255;
  }
  return new ImageData(arr, width, height);
}

export async function compareFilesLocal(fileA, fileB, onProgress) {
  if (!fileA || !fileB) throw new Error('Two files are required');

  onProgress && onProgress({ phase: 'decoding', message: 'Reading files…' });
  const [pagesA, pagesB] = await Promise.all([fileToPages(fileA), fileToPages(fileB)]);

  if (!pagesA.length || !pagesB.length) throw new Error('Could not read pages from one of the files');

  const originalA = pagesA.length;
  const originalB = pagesB.length;
  const maxPages = Math.max(pagesA.length, pagesB.length);

  while (pagesA.length < maxPages) {
    const ref = pagesB[pagesA.length];
    pagesA.push(blankImageData(ref.width, ref.height));
  }
  while (pagesB.length < maxPages) {
    const ref = pagesA[pagesB.length];
    pagesB.push(blankImageData(ref.width, ref.height));
  }

  const results = [];
  for (let i = 0; i < maxPages; i++) {
    onProgress && onProgress({
      phase: 'comparing',
      message: `Comparing page ${i + 1} of ${maxPages}…`,
      current: i + 1,
      total: maxPages,
    });
    // Yield to UI thread before each heavy page
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 0));
    // eslint-disable-next-line no-await-in-loop
    const res = await comparePagePair(pagesA[i], pagesB[i]);
    results.push(res);
  }

  return {
    success: true,
    page_count: maxPages,
    pages: results,
    file_a_pages: originalA,
    file_b_pages: originalB,
  };
}
